/**
 * 메뉴 아이템 도메인 타입 (canonical).
 *
 * #293: 이전에 `src/data/menuItems.ts`(mock 데이터 모듈)에 거주했으나 타입 레이어링
 * 관점에서 `@/types`가 `@/data`에 의존하는 역방향 참조를 야기했음(예:
 * `@/types/menuOption`이 `MenuItem`을 쓰려고 mock 모듈을 import). 본 파일로 이동하여
 * 도메인 타입/순수 predicate 헬퍼를 mock 데이터와 분리.
 */

import type { ApiMenuOptionGroup, MenuAvailabilityInfo, MenuDiscount } from '@/lib/api/types';

export interface MenuItem {
  id: string;
  nameKey: string;
  descriptionKey: string;
  price: number;
  originalPrice?: number;
  category: string;
  imageUrl?: string;
  /**
   * 판매 설정 (BE `MenuStatus::Active`와 매핑). 관리자가 메뉴를 노출 상태로 설정했는지 여부.
   * 시간/요일 기반의 "지금 주문 가능" 여부는 `availableDays`(mock) 또는 BE `is_available_at`로 판단.
   */
  isAvailable: boolean;
  /**
   * 품절 여부 (BE `MenuStatus::SoldOut`과 매핑). 관리자가 명시적으로 품절 처리한 경우에만 true.
   * `isAvailable=false`여도 `isSoldOut=false`일 수 있음(예: BE `Inactive` 상태) — 둘은 독립 축.
   * 판정은 반드시 {@link isMenuItemSoldOut} 헬퍼를 통해 수행(매퍼 이후 단일 경로 유지).
   */
  isSoldOut?: boolean;
  /** 인기 메뉴 여부 (BE `is_popular`와 매핑). HOT 배지 등 향후 활용 */
  isPopular?: boolean;
  /**
   * BE `discount` 응답: 할인율·할인액·타입 등.
   * 할인 여부 판정은 반드시 {@link hasDiscount} 헬퍼를 통해 수행 — 별도 boolean 플래그를
   * 두지 않음으로써 `isDiscounted`와의 불일치 상태를 타입 시스템상 차단 (#281).
   */
  discount?: MenuDiscount;
  availableDays?: number[]; // 0: Sunday, 1: Monday, ..., 6: Saturday
  /**
   * 실시간 가용성 정보 (BE `MenuResource.is_available_at`과 매핑).
   * API 경로(`src/hooks/usePickupMenuData.ts`의 매퍼)에서 주입되며, mock 경로에서는 부재.
   * 존재하면 `availability.available`이 "지금 주문 가능" 여부의 권위 있는 값.
   * 판정은 반드시 {@link isMenuItemAvailableNow} 헬퍼를 통해 수행.
   */
  availability?: MenuAvailabilityInfo;
  /**
   * #1439 G4 — 영업시간 외 표시 모드 (BE display_state 매핑).
   *
   * - 'hidden' → 메뉴 카드 미노출 (FE 가 필터). 기본 미설정은 'active' 동작과 동일.
   * - 'disabled' → 영업시간 외 grayed out + 주문 불가
   * - 'active' → 영업시간 외에도 정상 노출 (default)
   *
   * 판정은 반드시 {@link isMenuItemHiddenByDisplayMode} / {@link isMenuItemDisabledByDisplayMode} 헬퍼 사용.
   */
  displayState?: 'hidden' | 'disabled' | 'active';
  // 매장 ID (API 메뉴의 store_id를 매핑)
  restaurantId?: string;
  // 매장 이름 (API 메뉴의 store_name을 매핑)
  restaurantName?: string;
  // API 메뉴용 직접 이름/설명 필드
  _name?: string;
  _description?: string;
  // API 메뉴 옵션
  _options?: ApiMenuOptionGroup[];
  // 세트 메뉴 여부
  isSetMenu?: boolean;
  // 세트 메뉴 구성 아이템 수
  setMenuItemCount?: number;
}

/**
 * 메뉴 품절 판정 (단일 경로)
 *
 * BE `MenuStatus::SoldOut`을 그대로 신뢰한다. `isAvailable=false`만으로는 품절로 간주하지 않는다
 * (BE `Inactive` 상태는 품절이 아니라 단순 비활성이기 때문).
 */
export const isMenuItemSoldOut = (item: Pick<MenuItem, 'isSoldOut'>): boolean =>
  Boolean(item.isSoldOut);

/**
 * 메뉴 "지금 주문 가능" 판정 (단일 경로)
 *
 * 권위 우선순위: API 경로의 `availability.available` → mock 경로의 `availableDays` 기반 요일 판정.
 * `availability`도 `availableDays`도 없는 메뉴(즉 제한 없음)는 항상 가능으로 간주.
 * BE `MenuStatus` 자체의 활성 여부(`isAvailable`)나 품절(`isSoldOut`)은 이 판정과 독립이며,
 * 호출부에서 별도 조합(예: 클릭 차단 조건)해 사용해야 한다.
 */
export const isMenuItemAvailableNow = (
  item: Pick<MenuItem, 'availability' | 'availableDays'>,
  today: number = new Date().getDay(),
): boolean => {
  if (item.availability) return item.availability.available;
  return !item.availableDays || item.availableDays.includes(today);
};

/**
 * 메뉴 할인 판정 (단일 경로) — #281
 *
 * 할인 표시(배지·취소선 가격)의 유일한 진입 조건. `discount` 메타가 있거나
 * `originalPrice`(구 BE 호환 — `MenuResource`가 `finalOriginalPrice > discountedPrice`인
 * 경우에만 채움)가 양수이면 할인으로 간주. 0 이하는 BE에서 정상적으로 발생하지 않으며
 * 매퍼(`usePickupMenuData`)에서 폐기되므로, 본 헬퍼는 매퍼 통과 후 데이터를 가정.
 *
 * 별도 `isDiscounted` 플래그를 두지 않음으로써 두 필드 불일치 가능성을 제거.
 */
export const hasDiscount = (
  item: Pick<MenuItem, 'discount' | 'originalPrice'>,
): boolean =>
  item.discount != null ||
  (item.originalPrice != null && item.originalPrice > 0);

/**
 * #1439 G4 — 영업시간 외 표시 모드 hidden 판정 (단일 경로).
 *
 * 영업시간 외 + displayState='hidden' 인 경우 메뉴 카드 자체를 미노출.
 * BE 가 영업시간 내일 때는 displayState='active' 로 응답하므로,
 * 이 헬퍼는 BE 판정을 그대로 신뢰. 기본 미설정은 노출 (false).
 */
export const isMenuItemHiddenByDisplayMode = (
  item: Pick<MenuItem, 'displayState'>,
): boolean => item.displayState === 'hidden';

/**
 * #1439 G4 — 영업시간 외 표시 모드 disabled 판정 (단일 경로).
 *
 * displayState='disabled' 일 때 grayed out + 주문 불가. BE 응답을 그대로 신뢰.
 * 기본 미설정은 비활성 아님 (false).
 */
export const isMenuItemDisabledByDisplayMode = (
  item: Pick<MenuItem, 'displayState'>,
): boolean => item.displayState === 'disabled';

/** 메뉴 카테고리 식별자 목록 (mock/fallback) — 단일 진실 소스 */
export const menuCategories = ['appetizers', 'mains', 'sides', 'drinks', 'desserts'] as const;

/** 메뉴 카테고리 식별자 — `menuCategories`에서 파생하여 drift 방지 */
export type MenuCategoryId = (typeof menuCategories)[number];

/** API 카테고리 인터페이스 */
export interface MenuCategoryItem {
  id: string;
  nameKey: string;
  _name?: string;
}
